import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { XCircle, Trash2, Edit2, Save, X, Mail, Briefcase, MapPin, Calendar } from "lucide-react";
import type { User, UserPreferences, StockRegion } from "../../types";

interface AccessManagerModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: "partner" | "installer" | "commercial";
  items: User[];
  /** Message si le rechargement des comptes a échoué (liste peut être incomplète). */
  loadError?: string | null;
  /** Liste des régions (pour assigner un poseur à une région à la création). */
  stockRegions?: StockRegion[];
  onAdd: (data: {
    name: string;
    code: string;
    email?: string;
    role: "partner" | "installer" | "commercial";
    active: boolean;
    defaultStockRegionId?: string;
  }) => void;
  onUpdate: (id: string, data: { name?: string; email?: string; preferences?: UserPreferences }) => void;
  onRemove: (id: string) => void;
}

export function AccessManagerModal({
  isOpen,
  onClose,
  type,
  items,
  loadError,
  stockRegions = [],
  onAdd,
  onUpdate,
  onRemove,
}: AccessManagerModalProps) {
  const { t } = useTranslation();
  const [newName, setNewName] = useState("");
  const [newCode, setNewCode] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newDefaultRegionId, setNewDefaultRegionId] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState("");
  const [editEmail, setEditEmail] = useState("");

  const handleAdd = (e: React.FormEvent) => {
    e.preventDefault();
    onAdd({
      name: newName.trim(),
      code: newCode.trim().toUpperCase(), // Le préfixe sera ajouté automatiquement par le hook
      email: newEmail.trim() || undefined,
      role: type,
      active: true,
      ...(type === "installer" && newDefaultRegionId && { defaultStockRegionId: newDefaultRegionId }),
    });
    setNewName("");
    setNewCode("");
    setNewEmail("");
    setNewDefaultRegionId("");
  };

  const startEdit = (item: User) => {
    setEditingId(item.id);
    setEditName(item.name);
    setEditEmail(item.email || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName("");
    setEditEmail("");
  };

  const saveEdit = (id: string) => {
    onUpdate(id, {
      name: editName.trim(),
      email: editEmail.trim() || undefined,
    });
    cancelEdit();
  };

  const canSeePartner = (item: User) => item.preferences?.canSeePartner !== false;
  const setCanSeePartner = (item: User, value: boolean) => {
    onUpdate(item.id, {
      preferences: { ...item.preferences, canSeePartner: value },
    });
  };

  const canPartnerManageOwnSchedule = (item: User) =>
    item.preferences?.canPartnerManageOwnSchedule === true;
  const setCanPartnerManageOwnSchedule = (item: User, value: boolean) => {
    onUpdate(item.id, {
      preferences: { ...item.preferences, canPartnerManageOwnSchedule: value },
    });
  };

  if (!isOpen) return null;

  const modalContent = (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[1000]"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-slate-50 dark:bg-slate-900 border border-gray-200 dark:border-white/10 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden p-5"
        onClick={(e) => e.stopPropagation()}
      >
        {loadError && (
          <p className="mb-4 text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-xl px-3 py-2">
            {loadError}
          </p>
        )}
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-bold text-gray-900 dark:text-white">
            {type === "partner"
              ? t("access.manageCodesPartners")
              : type === "commercial"
                ? t("access.manageCodesCommercials")
                : t("access.manageCodesInstallers")}
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-slate-700 text-gray-500 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white transition-colors"
          >
            <XCircle size={20} />
          </button>
        </div>
        <form
          onSubmit={handleAdd}
          className="flex flex-col gap-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 shadow-sm p-4 rounded-xl mb-6"
        >
          <p className="text-xs text-gray-500 dark:text-slate-400 font-medium uppercase">
            {t("access.createAccessCode")}
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              placeholder={t("access.nameOrCompany")}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="flex-1 px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <input
              type="text"
              placeholder={t("access.codePlaceholder")}
              value={newCode}
              onChange={(e) => setNewCode(e.target.value)}
              className="w-full sm:w-32 px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 uppercase font-mono outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-400" />
            <input
              type="email"
              placeholder={t("access.emailRequiredRecovery")}
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              className="w-full pl-10 pr-3 py-2 bg-white dark:bg-slate-700 border border-blue-200 dark:border-blue-500/30 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 italic">
            💡 {t("access.prefixTip")}
          </p>
          <p className="text-xs text-blue-600 dark:text-blue-400 italic">
            📧 {t("access.emailTip")}
          </p>
          {type === "installer" && stockRegions.length > 0 && (
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600 dark:text-slate-400 flex items-center gap-1.5">
                <MapPin size={14} />
                {t("access.assignedRegionOptional")}
              </label>
              <select
                value={newDefaultRegionId}
                onChange={(e) => setNewDefaultRegionId(e.target.value)}
                className="w-full px-3 py-2.5 bg-white dark:bg-slate-900 border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500/30"
              >
                <option value="">{t("access.noRegion")}</option>
                {stockRegions.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          <button
            type="submit"
              className="w-full py-2.5 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-medium text-sm shadow-sm hover:opacity-95 transition-opacity"
          >
            {t("access.add")}
          </button>
        </form>
        <div className="space-y-2 max-h-60 overflow-y-auto">
          {items.map((item) => (
            <div
              key={item.id}
              className="p-3 bg-white dark:bg-slate-800 border border-gray-200 dark:border-white/10 rounded-xl shadow-sm"
            >
              {type === "partner" && (
                <div className="mb-3 pb-3 border-b border-gray-200 dark:border-slate-600 flex items-center justify-between w-full gap-3">
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2 min-w-0">
                    <Calendar size={16} className="text-slate-500 dark:text-slate-400 shrink-0" />
                    {t("access.partnerManageOwnSchedule")}
                  </span>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={canPartnerManageOwnSchedule(item)}
                    onClick={() => setCanPartnerManageOwnSchedule(item, !canPartnerManageOwnSchedule(item))}
                    className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
                      canPartnerManageOwnSchedule(item)
                        ? "bg-blue-600 dark:bg-blue-500"
                        : "bg-gray-300 dark:bg-slate-600"
                    }`}
                    title={t("access.partnerManageOwnScheduleTooltip")}
                  >
                    <span
                      className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-out ${
                        canPartnerManageOwnSchedule(item) ? "translate-x-5" : "translate-x-1"
                      }`}
                    />
                  </button>
                </div>
              )}
              {editingId === item.id ? (
                // Mode édition
                <div className="space-y-2">
                  <input
                    type="text"
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t("access.nameOrCompany")}
                  />
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-slate-400" />
                    <input
                      type="email"
                      value={editEmail}
                      onChange={(e) => setEditEmail(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-400 outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Email (REQUIS)"
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => saveEdit(item.id)}
                      className="flex-1 py-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-xl font-medium text-sm shadow-lg shadow-green-500/30 hover:opacity-95 transition-opacity flex items-center justify-center gap-2"
                    >
                      <Save size={16} />
                      {t("access.save")}
                    </button>
                    <button
                      type="button"
                      onClick={cancelEdit}
                      className="flex-1 py-2 bg-gray-200 dark:bg-slate-600 text-gray-700 dark:text-slate-300 rounded-xl font-medium text-sm hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors flex items-center justify-center gap-2"
                    >
                      <X size={16} />
                      {t("access.cancel")}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-slate-400 italic">
                    ℹ️ {t("access.codeCannotBeModified")}
                  </p>
                </div>
              ) : (
                // Mode affichage
                <>
                  <div className="flex justify-between items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-gray-900 dark:text-white">{item.name}</p>
                      <div className="flex flex-col gap-1 mt-1">
                        <span className="text-xs bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-300 px-2 py-0.5 rounded-lg font-mono w-fit">
                          {t("access.codeLabel")} {item.code}
                        </span>
                        {item.email ? (
                          <span className="text-xs text-gray-500 dark:text-slate-400 flex items-center gap-1">
                            <Mail size={12} />
                            {item.email}
                          </span>
                        ) : (
                          <span className="text-xs text-red-500 dark:text-red-400 font-medium italic">
                            ❌ {t("access.noEmailImpossible")}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0">
                      <button
                        type="button"
                        onClick={() => startEdit(item)}
                        className="p-2 text-gray-400 dark:text-slate-400 hover:text-blue-500 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-xl transition-colors"
                        title={t("access.edit")}
                      >
                        <Edit2 size={18} />
                      </button>
                      <button
                        type="button"
                        onClick={() => onRemove(item.id)}
                        className="p-2 text-gray-400 dark:text-slate-400 hover:text-red-500 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-xl transition-colors"
                        title={t("access.delete")}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                  {type === "installer" && (
                    <>
                      {stockRegions.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-600">
                          <label className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2 mb-2">
                            <MapPin size={16} className="text-slate-500 dark:text-slate-400 shrink-0" />
                            {t("access.assignedRegion")}
                          </label>
                          <select
                            value={item.preferences?.defaultStockRegionId ?? ""}
                            onChange={(e) => onUpdate(item.id, {
                              preferences: { ...item.preferences, defaultStockRegionId: e.target.value || undefined },
                            })}
                            className="w-full px-3 py-2 bg-white dark:bg-slate-700 border border-gray-200 dark:border-white/10 rounded-xl text-sm text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="">{t("access.noRegion")}</option>
                            {stockRegions.map((r) => (
                              <option key={r.id} value={r.id}>{r.name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      <div className="mt-3 pt-3 border-t border-gray-200 dark:border-slate-600 flex items-center justify-between w-full gap-3">
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-300 flex items-center gap-2 min-w-0">
                          <Briefcase size={16} className="text-slate-500 dark:text-slate-400 shrink-0" />
                          {t("access.seePartnerOnJobs")}
                        </span>
                        <button
                          type="button"
                          role="switch"
                          aria-checked={canSeePartner(item)}
                          onClick={() => setCanSeePartner(item, !canSeePartner(item))}
                          className={`relative inline-flex h-7 w-12 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-slate-800 ${
                            canSeePartner(item)
                              ? "bg-blue-600 dark:bg-blue-500"
                              : "bg-gray-300 dark:bg-slate-600"
                          }`}
                          title={canSeePartner(item) ? t("access.disablePartnerTooltip") : t("access.enablePartnerTooltip")}
                        >
                          <span
                            className={`pointer-events-none inline-block h-6 w-6 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-out ${
                              canSeePartner(item) ? "translate-x-5" : "translate-x-1"
                            }`}
                          />
                        </button>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
